/**
 * tierIntelligence.ts — the public intelligence score shown on each debate tier.
 *
 * Data model:
 *   config/tierIntelligence → { generatedAt, tiers: { free?, plus?, pro? } }
 *
 * The daily model roster refresh writes this doc; the frontend only reads.
 */

import { doc, onSnapshot, Unsubscribe } from "firebase/firestore";
import { db } from "./config";

export type TierName = "free" | "plus" | "pro";

export interface TierIntelligenceStat {
  score: number;
  modelCount: number;
}

export interface TierIntelligence {
  generatedAt: string;
  tiers: Partial<Record<TierName, TierIntelligenceStat>>;
}

const isScore = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100;

export function parseTierIntelligence(data: any): TierIntelligence | null {
  if (typeof data?.generatedAt !== "string") return null;
  const tiers: TierIntelligence["tiers"] = {};
  for (const name of ["free", "plus", "pro"] as const) {
    const stat = data.tiers?.[name];
    if (isScore(stat?.score) && typeof stat?.modelCount === "number" && stat.modelCount > 0) {
      tiers[name] = { score: stat.score, modelCount: stat.modelCount };
    }
  }
  return { generatedAt: data.generatedAt, tiers };
}

/** Subscribe to the tier scores. Null means no score to show, including on read errors. */
export function onTierIntelligenceSnapshot(
  callback: (intelligence: TierIntelligence | null) => void,
): Unsubscribe {
  return onSnapshot(
    doc(db, "config", "tierIntelligence"),
    (snap) => callback(snap.exists() ? parseTierIntelligence(snap.data()) : null),
    () => callback(null),
  );
}
